import { useState, useEffect, useMemo } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useAuth } from "AuthContext";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Checkbox, FormControlLabel, FormGroup, Box, Divider,
} from "@mui/material";
import { fmtDateTime, fmtDate } from "utils/datetime";
import * as XLSX from "xlsx";

// 2 碼國碼 → 國旗 emoji（A→🇦 …）
const flagEmoji = (cc) =>
  cc && /^[A-Za-z]{2}$/.test(cc)
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : "";

// 2 碼國碼 → 國家全名（台灣 / 美國 …，不顯示像語系的 TW/tw）
let _regionNames;
try {
  _regionNames = new Intl.DisplayNames(["zh-TW"], { type: "region" });
} catch (e) {
  _regionNames = null;
}
const countryName = (cc) => {
  if (!cc) return "";
  const code = String(cc).toUpperCase();
  if (/^[A-Z]{2}$/.test(code) && _regionNames) {
    try {
      return _regionNames.of(code) || code;
    } catch (e) {
      return code;
    }
  }
  return String(cc); // 非 2 碼（如 phoneCountry 其他格式）原樣
};

function UserManager() {
  const { token, user } = useAuth();
  const isSuperAdmin = user?.role === "ADMIN"; // 只有 ADMIN 能設定他人權限
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [openBan, setOpenBan] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [banDays, setBanDays] = useState(0);

  // 權限設定對話框
  const [openPerm, setOpenPerm] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permRole, setPermRole] = useState("USER");
  const [permFeatures, setPermFeatures] = useState([]);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [permSaving, setPermSaving] = useState(false);
  const [myAccess, setMyAccess] = useState(null); // 目前管理員自己的 {role, adminFeatures}

  // ✉️ 寄信對話框（行銷/客服直接聯絡使用者；每封都會寫入 useremaillog）
  const [openMail, setOpenMail] = useState(false);
  const [mailUser, setMailUser] = useState(null);
  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [mailLang, setMailLang] = useState("zh-TW"); // 信件語系（決定品牌外殼＋信尾翻譯）
  const [mailSending, setMailSending] = useState(false);
  const [mailHistory, setMailHistory] = useState([]);
  const [mailOpenId, setMailOpenId] = useState(null); // 歷史紀錄展開中的那一筆（看完整內容）

  // 使用追蹤（操作紀錄）對話框
  const [openActivity, setOpenActivity] = useState(false);
  const [actUser, setActUser] = useState(null);
  const [actItems, setActItems] = useState([]);
  const [actTotal, setActTotal] = useState(0);
  const [actPage, setActPage] = useState(1);
  const [actCategory, setActCategory] = useState("important");
  const [actFrom, setActFrom] = useState("");
  const [actTo, setActTo] = useState("");
  const [actLoading, setActLoading] = useState(false);
  const ACT_PAGE_SIZE = 20;

  // 登入安全紀錄對話框
  const [openLogin, setOpenLogin] = useState(false);
  const [loginUser, setLoginUser] = useState(null);
  const [loginItems, setLoginItems] = useState([]);
  const [loginTotal, setLoginTotal] = useState(0);
  const [loginPage, setLoginPage] = useState(1);
  const [loginLoading, setLoginLoading] = useState(false);
  const LOGIN_PAGE_SIZE = 20;

  const authHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` });

  const openPermDialog = async (u) => {
    setPermUser(u);
    setOpenPerm(true);
    try {
      const [catRes, permRes] = await Promise.all([
        fetch("/api/admin/permissions/features", { headers: authHeaders() }).then((r) => r.json()),
        fetch(`/api/admin/permissions/user/${u.id}`, { headers: authHeaders() }).then((r) => r.json()),
      ]);
      setFeatureCatalog(catRes?.features || []);
      setPermRole(permRes?.role || "USER");
      setPermFeatures(permRes?.adminFeatures || []);
    } catch (e) {
      console.error("load permissions error:", e);
    }
  };

  const toggleFeature = (key) =>
    setPermFeatures((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const savePerm = async () => {
    setPermSaving(true);
    try {
      const res = await fetch(`/api/admin/permissions/user/${permUser.id}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ role: permRole, features: permFeatures }),
      });
      const data = await res.json();
      if (res.ok) {
        setOpenPerm(false);
        fetchUsers();
      } else {
        window.alert(data?.message || "設定失敗");
      }
    } catch (e) {
      window.alert("設定失敗");
    } finally {
      setPermSaving(false);
    }
  };

  // 使用者當地日界 → ISO 瞬間（後端直接比對 createdAt UTC）
  const dayStartISO = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : "");
  const dayEndISO = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : "");

  const fetchActivity = async (u, { page = 1, category, from, to } = {}) => {
    if (!u) return;
    setActLoading(true);
    const cat = category ?? actCategory;
    const f = from ?? actFrom;
    const t = to ?? actTo;
    const qs = new URLSearchParams({ page: String(page), limit: String(ACT_PAGE_SIZE), category: cat });
    if (f) qs.set("from", dayStartISO(f));
    if (t) qs.set("to", dayEndISO(t));
    try {
      const res = await fetch(`/api/admin/users/${u.id}/activity?${qs.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setActItems(data.items || []);
        setActTotal(data.total || 0);
        setActPage(data.page || page);
      } else {
        setActItems([]); setActTotal(0);
        window.alert(data?.message || "讀取失敗");
      }
    } catch (e) {
      setActItems([]); setActTotal(0);
      console.error("fetch activity error:", e);
    } finally {
      setActLoading(false);
    }
  };

  // 寄信：開視窗時順便載入這位使用者的歷史寄信紀錄
  const openMailDialog = async (u) => {
    setMailUser(u);
    setMailSubject(""); setMailBody(""); setMailHistory([]); setMailOpenId(null);
    // 預設用該使用者的語系（管理員仍可在視窗改）
    const uloc = ["zh-TW", "zh-CN", "en"].includes(u.locale) ? u.locale : "zh-TW";
    setMailLang(uloc);
    setOpenMail(true);
    try {
      const res = await fetch(`/api/admin/user-emails?userId=${encodeURIComponent(u.id)}&limit=200`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setMailHistory(data.items || []);
    } catch (e) { console.error("fetch mail history error:", e); }
  };

  const handleSendMail = async () => {
    if (!mailUser) return;
    if (!mailSubject.trim() || !mailBody.trim()) { window.alert("標題與內容為必填"); return; }
    setMailSending(true);
    try {
      const res = await fetch("/api/admin/user-emails", {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ userId: mailUser.id, subject: mailSubject, body: mailBody, lang: mailLang }),
      });
      const data = await res.json();
      if (res.ok) {
        window.alert(`已寄出至 ${data.to || mailUser.email}`);
        setOpenMail(false);
      } else {
        window.alert(data?.message || "寄送失敗");
      }
    } catch (e) {
      window.alert("寄送失敗，請稍後再試");
    } finally {
      setMailSending(false);
    }
  };

  const openActivityDialog = (u) => {
    setActUser(u);
    setOpenActivity(true);
    setActItems([]); setActTotal(0); setActPage(1);
    setActFrom(""); setActTo(""); setActCategory("important");
    fetchActivity(u, { page: 1, category: "important", from: "", to: "" });
  };

  // 詳情 metadata → 精簡字串（middleware 寫的是 {path,method,status}）
  const fmtActMeta = (m) => {
    if (!m || typeof m !== "object") return "";
    if (m.path) {
      const head = `${m.method ? `${m.method} ` : ""}${m.path}`;
      const tail = m.status ? ` · ${m.status}` : m.title ? ` · ${m.title}` : "";
      return (head + tail).trim();
    }
    const keys = Object.keys(m);
    return keys.length ? keys.map((k) => `${k}: ${m[k]}`).join(", ") : "";
  };

  const fetchLoginHistory = async (u, { page = 1 } = {}) => {
    if (!u) return;
    setLoginLoading(true);
    try {
      const res = await fetch(`/api/admin/users/${u.id}/login-history?page=${page}&limit=${LOGIN_PAGE_SIZE}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setLoginItems(data.items || []);
        setLoginTotal(data.total || 0);
        setLoginPage(data.page || page);
      } else {
        setLoginItems([]); setLoginTotal(0);
        window.alert(data?.message || "讀取失敗");
      }
    } catch (e) {
      setLoginItems([]); setLoginTotal(0);
      console.error("fetch login history error:", e);
    } finally {
      setLoginLoading(false);
    }
  };

  const openLoginDialog = (u) => {
    setLoginUser(u);
    setOpenLogin(true);
    setLoginItems([]); setLoginTotal(0); setLoginPage(1);
    fetchLoginHistory(u, { page: 1 });
  };

  // device 欄位可能是 EXE 機器資訊 JSON → 只顯示「平台 · 主機名」
  const fmtDevice = (raw) => {
    if (!raw) return "—";
    const s = String(raw).trim();
    if (s.startsWith("{")) {
      try {
        const p = JSON.parse(s);
        const plat = (p.platform || "").split("-").slice(0, 2).join(" ");
        const host = p.hostname || "";
        if (plat && host) return `${plat} · ${host}`;
        return plat || host || s;
      } catch {
        return s;
      }
    }
    return s;
  };

  const fetchUsers = () => {
    fetch(`/api/admin/users?search=${search}&limit=1000`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setUsers(data.users || []))
      .catch((err) => console.error("Fetch users error:", err));
  };

  useEffect(() => {
    fetchUsers();
  }, [token, search]);

  // 讀自己的後台權限 → 決定能不能顯示「封鎖」（後端另有 users-ban 權限守衛把關）
  useEffect(() => {
    if (!token) return;
    fetch("/api/users/admin-access", { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setMyAccess(d))
      .catch(() => setMyAccess(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleToggleBan = () => {
    let banUntil = null;
    if (banDays > 0) {
      const date = new Date();
      date.setDate(date.getDate() + parseInt(banDays));
      banUntil = date.toISOString();
    } else if (banDays === -1) {
      banUntil = "2099-12-31T23:59:59Z"; // 永久封鎖
    }

    fetch("/api/admin/users/toggle-ban", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        userId: selectedUser.id,
        banUntil,
        banReason,
      }),
    })
      .then(() => {
        setOpenBan(false);
        fetchUsers();
      });
  };

  // 距今天數（最近使用欄位用）：回 { days, label }，無資料回 null
  const daysAgo = (iso) => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return null;
    const d = Math.floor((Date.now() - t) / 86400000);
    return { days: d, label: d <= 0 ? "今天" : d === 1 ? "昨天" : `${d} 天前` };
  };

  // 匯出 Excel（用目前載入的用戶清單；國家顯示全名）
  const handleExport = () => {
    const data = users.map((u) => ({
      姓名: u.fullName || "",
      信箱: u.email || "",
      角色: u.role || "",
      方案: u.plan?.name || u.planCode || "",
      餘額: u.coinwallet?.balance ?? 0,
      "國家/地區": u.country ? countryName(u.country) : (u.phoneCountry ? countryName(u.phoneCountry) : ""),
      電話: [u.phoneCountry, u.phoneNumber].filter(Boolean).join(" "),
      建立時間: fmtDateTime(u.createdAt),
      最近使用: u.lastActiveAt ? fmtDateTime(u.lastActiveAt) : "從未使用",
      距今天數: daysAgo(u.lastActiveAt)?.days ?? "",
      狀態: u.banUntil ? `封鎖至 ${fmtDate(u.banUntil)}` : "正常",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "用戶");
    XLSX.writeFile(wb, `users_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // 能否封鎖：ADMIN 或有 users-ban 權限（行銷人員沒有 → 看得到用戶但不能封鎖）
  const canBan = isSuperAdmin || (myAccess?.adminFeatures || []).includes("users-ban");
  // 能否寄信：ADMIN 或有 users-email 權限（後端 adminAccessGuard 才是真正把關）
  const canMail = isSuperAdmin || (myAccess?.adminFeatures || []).includes("users-email");

  // 用 useMemo 讓 columns/rows 參考穩定 → 開關 modal 重 render 時 react-table 不會把頁碼歸零
  const columns = useMemo(() => [
    { Header: "姓名", accessor: "fullName", width: "15%" },
    { Header: "信箱", accessor: "email", width: "20%" },
    { Header: "角色", accessor: "role", width: "10%" },
    { Header: "餘額", accessor: "coinwallet.balance", width: "10%" },
    { Header: "方案", accessor: "plan.name", width: "12%" },
    {
      Header: "國家/地區",
      accessor: "country",
      width: "10%",
      // 依最新登入 IP 定位（geoip）；沒有則退回註冊填的 phoneCountry
      Cell: ({ row }) => {
        const c = row.original.country;
        const pc = row.original.phoneCountry;
        return (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {c ? `${flagEmoji(c)} ${countryName(c)}` : (pc ? countryName(pc) : "—")}
          </MDTypography>
        );
      },
    },
    {
      Header: "創建日期",
      accessor: "createdAt",
      width: "13%",
      // createdAt 從 API 出來是 UTC instant（ISO ...Z）；用共用 util 轉「使用者當地時區」顯示
      Cell: ({ value }) => (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {fmtDateTime(value)}
        </MDTypography>
      ),
    },
    {
      // 最近一次操作/瀏覽（= 使用追蹤最新那筆）；行銷用來判斷誰還活著、免得逐一點開
      Header: "最近使用",
      accessor: "lastActiveAt",
      width: "13%",
      Cell: ({ value }) => {
        const ago = daysAgo(value);
        if (!ago) return (
          <MDTypography variant="caption" color="secondary" fontWeight="medium">從未使用</MDTypography>
        );
        // 7 天內綠、30 天內橘、更久灰 → 一眼看出活躍度
        const color = ago.days <= 7 ? "success" : ago.days <= 30 ? "warning" : "secondary";
        return (
          <Box>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
              {fmtDateTime(value)}
            </MDTypography>
            <MDTypography variant="caption" color={color} fontWeight="bold">
              {ago.label}
            </MDTypography>
          </Box>
        );
      },
    },
    {
      Header: "狀態",
      accessor: "banUntil",
      Cell: ({ value }) => (
        <MDTypography variant="caption" color={value ? "error" : "success"} fontWeight="medium">
          {value ? `封鎖至 ${new Date(value).toLocaleDateString()}` : "正常"}
        </MDTypography>
      )
    },
    {
      Header: "操作",
      accessor: "id",
      Cell: ({ row }) => (
        <Box display="flex" gap={0.5}>
          {canBan && (
            <MDButton
              variant="text"
              color={row.original.banUntil ? "success" : "error"}
              onClick={() => {
                setSelectedUser(row.original);
                setBanReason(row.original.banReason || "");
                setOpenBan(true);
              }}
            >
              {row.original.banUntil ? "解封" : "封鎖"}
            </MDButton>
          )}
          {canMail && (
            <MDButton variant="text" color="info" onClick={() => openMailDialog(row.original)}>
              寄信
            </MDButton>
          )}
          <MDButton variant="text" color="dark" onClick={() => openActivityDialog(row.original)}>
            使用追蹤
          </MDButton>
          <MDButton variant="text" color="dark" onClick={() => openLoginDialog(row.original)}>
            登入紀錄
          </MDButton>
          {isSuperAdmin && (
            <MDButton variant="text" color="info" onClick={() => openPermDialog(row.original)}>
              權限
            </MDButton>
          )}
        </Box>
      )
    }
  ], [canBan, canMail, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => users.map(user => ({
    ...user,
    "coinwallet.balance": user.coinwallet?.balance || 0,
    "plan.name": user.plan?.name || user.planCode
  })), [users]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  用戶管理中心
                </MDTypography>
                <MDBox width="12rem" ml="auto">
                  <MDInput
                    label="搜尋用戶..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                  />
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                <Box display="flex" justifyContent="flex-end" px={2} pb={1}>
                  <MDButton color="info" variant="gradient" size="small" onClick={handleExport} disabled={!users.length}>
                    匯出 Excel
                  </MDButton>
                </Box>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* 權限設定對話框（ADMIN-only） */}
      <Dialog open={openPerm} onClose={() => setOpenPerm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>設定後台權限 — {permUser?.fullName || permUser?.email}</DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          <Box pt={1} display="flex" flexDirection="column" gap={2}>
            <TextField
              select
              label="角色"
              value={permRole}
              onChange={(e) => setPermRole(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="USER">一般使用者（無後台）</MenuItem>
              <MenuItem value="STAFF">員工 / 客服（限定功能）</MenuItem>
              <MenuItem value="ADMIN">管理員（全部功能）</MenuItem>
            </TextField>

            {permRole === "STAFF" && (
              <Box>
                <Box mb={1}>
                  <MDButton size="small" variant="outlined" color="info"
                    onClick={() => setPermFeatures(["users", "analytics", "ops-analytics", "promo-codes", "support"])}>
                    套用「行銷人員」預設
                  </MDButton>
                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    行銷人員 = 用戶管理（不含封鎖）＋使用熱力＋營運分析＋優惠碼＋客服；不含封鎖用戶與權限設定。
                  </MDTypography>
                </Box>
                <MDTypography variant="button" fontWeight="medium">可用的後台功能</MDTypography>
                <FormGroup>
                  {featureCatalog.map((f) => (
                    <FormControlLabel
                      key={f.key}
                      control={
                        <Checkbox
                          checked={permFeatures.includes(f.key)}
                          onChange={() => toggleFeature(f.key)}
                        />
                      }
                      label={f.label}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
            {permRole === "ADMIN" && (
              <MDTypography variant="caption" color="text">管理員可使用所有後台功能，並能設定他人權限。</MDTypography>
            )}
            {permRole === "USER" && (
              <MDTypography variant="caption" color="text">一般使用者不會看到任何後台功能。</MDTypography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton onClick={() => setOpenPerm(false)} color="secondary">取消</MDButton>
          <MDButton onClick={savePerm} color="info" variant="gradient" disabled={permSaving}>
            {permSaving ? "儲存中..." : "儲存"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={openBan} onClose={() => setOpenBan(false)}>
        <DialogTitle>{selectedUser?.banUntil ? "解除封鎖" : "封鎖用戶"}</DialogTitle>
        <DialogContent>
          <MDBox pt={2} display="flex" flexDirection="column">
            {!selectedUser?.banUntil && (
              <>
                <MDInput
                  type="number"
                  label="封鎖天數 (0 為永久)"
                  value={banDays}
                  onChange={(e) => setBanDays(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <MDTypography variant="caption" color="text">輸入 0 代表永久封鎖</MDTypography>
              </>
            )}
            <MDInput
              label="封鎖/操作原因"
              multiline
              rows={3}
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenBan(false)} color="secondary">取消</MDButton>
          <MDButton onClick={handleToggleBan} color={selectedUser?.banUntil ? "success" : "error"}>
            確認{selectedUser?.banUntil ? "解封" : "封鎖"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✉️ 寄信給使用者（行銷/客服）；每封（含失敗）都寫入 useremaillog 供日後查詢 */}
      <Dialog open={openMail} onClose={() => !mailSending && setOpenMail(false)} maxWidth="sm" fullWidth>
        <DialogTitle>寄信給 {mailUser?.fullName || mailUser?.email}</DialogTitle>
        <DialogContent>
          <MDBox mt={1} mb={2}>
            <MDTypography variant="caption" color="text">
              收件者：{mailUser?.email}　·　寄件者顯示為 MasQuant 品牌信，使用者可直接回覆
            </MDTypography>
          </MDBox>
          <TextField
            select label="信件語系" fullWidth value={mailLang}
            onChange={(e) => setMailLang(e.target.value)}
            helperText="決定信件外框與信尾（如需協助…／公司介紹）的語言"
            sx={{ mb: 2, "& .MuiInputBase-root": { height: 44 } }}
          >
            <MenuItem value="zh-TW">繁體中文</MenuItem>
            <MenuItem value="zh-CN">简体中文</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </TextField>
          <TextField
            label="標題" fullWidth value={mailSubject}
            onChange={(e) => setMailSubject(e.target.value)}
            inputProps={{ maxLength: 255 }} sx={{ mb: 2 }}
          />
          <TextField
            label="內容" fullWidth multiline minRows={8} value={mailBody}
            onChange={(e) => setMailBody(e.target.value)}
            placeholder="直接輸入純文字即可，換行會保留。"
          />

          {mailHistory.length > 0 && (
            <MDBox mt={3}>
              <Divider sx={{ mb: 1.5 }} />
              <MDTypography variant="button" fontWeight="medium">
                過往寄信紀錄（共 {mailHistory.length} 筆）
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                點任一筆可展開看當時寄出的完整內容
              </MDTypography>
              <MDBox mt={0.5} sx={{ maxHeight: 300, overflowY: "auto" }}>
                {mailHistory.map((h) => {
                  const opened = mailOpenId === h.id;
                  return (
                    <MDBox key={h.id} py={0.75} borderBottom="1px solid #eee">
                      <MDBox
                        onClick={() => setMailOpenId(opened ? null : h.id)}
                        sx={{ cursor: "pointer", "&:hover": { opacity: 0.75 } }}
                      >
                        <MDTypography variant="caption" color="text" display="block">
                          {fmtDateTime(h.createdAt)}　·　{h.sentByEmail}{h.lang ? `　·　${h.lang}` : ""}
                          {h.status === "FAILED" && (
                            <MDTypography variant="caption" color="error" fontWeight="bold"> · 寄送失敗</MDTypography>
                          )}
                        </MDTypography>
                        <MDTypography variant="caption" fontWeight="medium" display="block">
                          {opened ? "▾ " : "▸ "}{h.subject}
                        </MDTypography>
                      </MDBox>

                      {opened && (
                        <MDBox mt={1} p={1.5} sx={{ background: "#f8f9fa", borderRadius: 1 }}>
                          <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                            收件信箱：{h.toEmail}
                          </MDTypography>
                          {h.status === "FAILED" && h.error && (
                            <MDTypography variant="caption" color="error" display="block" mb={0.5}>
                              失敗原因：{h.error}
                            </MDTypography>
                          )}
                          {/* 原文照登：保留當初寄出的換行 */}
                          <MDTypography
                            variant="caption"
                            component="div"
                            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.7 }}
                          >
                            {h.body}
                          </MDTypography>
                          <MDBox mt={1}>
                            <MDButton
                              size="small" variant="text" color="info"
                              onClick={() => { setMailSubject(h.subject); setMailBody(h.body); setMailLang(h.lang || "zh-TW"); }}
                            >
                              以此為範本填入上方
                            </MDButton>
                          </MDBox>
                        </MDBox>
                      )}
                    </MDBox>
                  );
                })}
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenMail(false)} color="secondary" disabled={mailSending}>取消</MDButton>
          <MDButton onClick={handleSendMail} color="info" disabled={mailSending}>
            {mailSending ? "寄送中…" : "寄出"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 使用追蹤（操作紀錄時間軸；讀 auditlog，時間走 utils/datetime 依觀看者時區） */}
      <Dialog open={openActivity} onClose={() => setOpenActivity(false)} maxWidth="md" fullWidth>
        <DialogTitle>使用追蹤 — {actUser?.fullName || actUser?.email}</DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          <Box pt={1} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <TextField
              type="date" label="起始日" InputLabelProps={{ shrink: true }} size="small"
              value={actFrom} onChange={(e) => setActFrom(e.target.value)}
            />
            <TextField
              type="date" label="結束日" InputLabelProps={{ shrink: true }} size="small"
              value={actTo} onChange={(e) => setActTo(e.target.value)}
            />
            <TextField
              select label="類別" size="small" sx={{ minWidth: 150 }}
              value={actCategory} onChange={(e) => setActCategory(e.target.value)}
            >
              <MenuItem value="important">重要動作</MenuItem>
              <MenuItem value="all">全部動作（含讀取）</MenuItem>
              <MenuItem value="page">頁面瀏覽</MenuItem>
            </TextField>
            <MDButton color="info" variant="gradient" size="small"
              onClick={() => fetchActivity(actUser, { page: 1 })} disabled={actLoading}>
              {actLoading ? "查詢中..." : "查詢"}
            </MDButton>
          </Box>

          <MDBox mt={2}>
            {actLoading ? (
              <MDTypography variant="button" color="text">讀取中...</MDTypography>
            ) : actItems.length === 0 ? (
              <MDTypography variant="button" color="text">此區間查無紀錄</MDTypography>
            ) : (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ textAlign: "left", color: "text.secondary" }}>
                    <Box component="th" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>時間</Box>
                    <Box component="th" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>動作</Box>
                    <Box component="th" sx={{ py: 1 }}>詳情</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {actItems.map((it) => (
                    <Box component="tr" key={it.id} sx={{ borderTop: "1px solid #eef0f3" }}>
                      <Box component="td" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>
                        {fmtDateTime(it.createdAt, { withSeconds: true })}
                      </Box>
                      <Box component="td" sx={{ py: 1, pr: 2, whiteSpace: "nowrap", fontWeight: 500 }}>
                        {it.action}
                      </Box>
                      <Box component="td" sx={{ py: 1, color: "text.secondary", wordBreak: "break-all" }}>
                        {fmtActMeta(it.metadata)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <MDTypography variant="caption" color="text">
            共 {actTotal} 筆 · 第 {actPage} / {Math.max(1, Math.ceil(actTotal / ACT_PAGE_SIZE))} 頁
          </MDTypography>
          <Box display="flex" gap={1}>
            <MDButton size="small" color="secondary"
              disabled={actLoading || actPage <= 1}
              onClick={() => fetchActivity(actUser, { page: actPage - 1 })}>
              上一頁
            </MDButton>
            <MDButton size="small" color="secondary"
              disabled={actLoading || actPage >= Math.ceil(actTotal / ACT_PAGE_SIZE)}
              onClick={() => fetchActivity(actUser, { page: actPage + 1 })}>
              下一頁
            </MDButton>
            <MDButton color="dark" onClick={() => setOpenActivity(false)}>關閉</MDButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* 登入安全紀錄（後台版；讀 loginhistory，比照使用者端「安全紀錄」） */}
      <Dialog open={openLogin} onClose={() => setOpenLogin(false)} maxWidth="md" fullWidth>
        <DialogTitle>登入紀錄 — {loginUser?.fullName || loginUser?.email}</DialogTitle>
        <Divider sx={{ m: 0 }} />
        <DialogContent>
          <MDBox mt={1}>
            {loginLoading ? (
              <MDTypography variant="button" color="text">讀取中...</MDTypography>
            ) : loginItems.length === 0 ? (
              <MDTypography variant="button" color="text">查無登入紀錄</MDTypography>
            ) : (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ textAlign: "left", color: "text.secondary" }}>
                    <Box component="th" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>時間</Box>
                    <Box component="th" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>IP</Box>
                    <Box component="th" sx={{ py: 1, pr: 2 }}>裝置</Box>
                    <Box component="th" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>平台</Box>
                    <Box component="th" sx={{ py: 1, whiteSpace: "nowrap" }}>動作</Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {loginItems.map((it) => (
                    <Box component="tr" key={it.id} sx={{ borderTop: "1px solid #eef0f3" }}>
                      <Box component="td" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>
                        {fmtDateTime(it.createdAt, { withSeconds: true })}
                      </Box>
                      <Box component="td" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>{it.ipAddress || "—"}</Box>
                      <Box component="td"
                        sx={{ py: 1, pr: 2, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={it.device || ""}>
                        {fmtDevice(it.device)}
                      </Box>
                      <Box component="td" sx={{ py: 1, pr: 2, whiteSpace: "nowrap" }}>{it.platform || "—"}</Box>
                      <Box component="td" sx={{ py: 1, whiteSpace: "nowrap", fontWeight: 500 }}>{it.action || "—"}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <MDTypography variant="caption" color="text">
            共 {loginTotal} 筆 · 第 {loginPage} / {Math.max(1, Math.ceil(loginTotal / LOGIN_PAGE_SIZE))} 頁
          </MDTypography>
          <Box display="flex" gap={1}>
            <MDButton size="small" color="secondary"
              disabled={loginLoading || loginPage <= 1}
              onClick={() => fetchLoginHistory(loginUser, { page: loginPage - 1 })}>
              上一頁
            </MDButton>
            <MDButton size="small" color="secondary"
              disabled={loginLoading || loginPage >= Math.ceil(loginTotal / LOGIN_PAGE_SIZE)}
              onClick={() => fetchLoginHistory(loginUser, { page: loginPage + 1 })}>
              下一頁
            </MDButton>
            <MDButton color="dark" onClick={() => setOpenLogin(false)}>關閉</MDButton>
          </Box>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default UserManager;
